import { useEffect, useCallback } from "react"

import { Theme } from "@mui/material"
import * as htmlToImage from "html-to-image"

import { DEFAULT_SIGN_CODE, DEFAULT_SIGN_ICON, Source, useMapDispatchContext } from "@l2r-front/l2r-map"
import { useTheme } from "@l2r-front/l2r-ui"

import { DISABLED_STATE, ENABLED_STATE } from "../../utils/getCatalogItemLayerStyle"


const ICON_SIZE = 60

declare global {
    interface Window {
        generatedIcons?: { [key: string]: string }
    }
}

type CatalogItemsSourceProps = {
    getIcon: (_typeCode: string) => string
    iconOffset?: number
    legend: any
    sanitizedData: GeoJSON.FeatureCollection<GeoJSON.Geometry>
    types: Array<string>
}

export function CatalogItemsSource(props: CatalogItemsSourceProps) {

    const {
        getIcon,
        iconOffset = 10,
        legend,
        sanitizedData,
        types,
        ...otherProps
    } = props

    const theme = useTheme()
    const { getMapRef } = useMapDispatchContext()

    const createMapIcon = useCallback((code: string, icon: string, condition: string, state: any, map: any, theme: Theme) => {
        const color = legend[condition]?.color
        const opacity = state === ENABLED_STATE ? 1 : 0.5
        const iconId = code + "#" + condition + "#" + state
        const container = document.createElement("div")
        container.innerHTML = `
            <div style="background-color: white; height: 100%; width: 100%; border-radius: 50%;">
                <div style="background-color: ${color}; opacity:${opacity}; height: 100%; width: 100%; border-radius: 50%; position:absolute;top:0px;left:0px"></div>
                <div style="background-color: white; height: calc(100% - 20px); width: calc(100% - 20px); border-radius: 50%; position:absolute;top:10px;left:10px">
                    <img src=${icon} style="width:calc(100% - ${iconOffset}px); height:calc(100% - ${iconOffset}px); position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);object-fit: contain; opacity:${opacity};" />
                </div>
            </div>
        `

        htmlToImage.toPng(container, {
            height: ICON_SIZE,
            width: ICON_SIZE,
            canvasHeight: ICON_SIZE,
            canvasWidth: ICON_SIZE,
            pixelRatio: 1,
            skipFonts: true,
        })
            .then(function (dataUrl) {
                window.generatedIcons[iconId] = dataUrl
                map.loadImage(
                    dataUrl,
                    (error, icon) => {
                        if (error) {
                            return console.error(error)
                        }
                        if (!map.hasImage(iconId)) {
                            map.addImage(iconId, icon)
                        }
                    },
                )
            })
            .catch(() => {
                createMapIcon(code, DEFAULT_SIGN_ICON, condition, state, map, theme)
            })
    }, [iconOffset, legend])

    useEffect(() => {
        const map = getMapRef()?.getMap()

        if (!map || !theme || !types) {
            return undefined
        }

        Object.keys(legend).forEach(condition => {
            [ENABLED_STATE, DISABLED_STATE].forEach(state => {
                const iconId = DEFAULT_SIGN_CODE + "#" + condition + "#" + state
                if (!window.generatedIcons) {
                    window.generatedIcons = {}
                }

                if (!window.generatedIcons[iconId]) {
                    createMapIcon(DEFAULT_SIGN_CODE, DEFAULT_SIGN_ICON, condition, state, map, theme)
                }
            })
        })

        interface StyleImageMissingEvent {
            id: string
            [key: string]: any
        }

        interface MapImage {
            width: number
            height: number
            data: Uint8Array | HTMLImageElement | ImageBitmap
        }

        const loadIcon = (e: StyleImageMissingEvent): void | undefined => {
            const iconId: string = e.id
            const [typeCode = "", condition = "", state = ""] = iconId.split("#")

            if (!typeCode || Object.keys(legend).indexOf(condition) < 0) {
                return undefined
            }

            if (!window.generatedIcons) {
                window.generatedIcons = {}
            }

            if (window.generatedIcons[iconId]) {
                map.loadImage(
                    window.generatedIcons[iconId],
                    (error: Error | null, icon?: MapImage) => {
                        if (error) {
                            console.error(error)
                            return
                        }

                        if (!map.hasImage(iconId)) {
                            map.addImage(iconId, icon)
                        }
                    },
                )
            } else {
                const icon: string = getIcon(typeCode)
                createMapIcon(typeCode, icon, condition, state, map, theme)
            }
            return undefined
        }

        map?.on("styleimagemissing", loadIcon)

        return () => map?.off("styleimagemissing", loadIcon)

    }, [createMapIcon, getIcon, getMapRef, theme, types, legend])

    if (!sanitizedData) {
        return null
    }

    return (
        <Source {...otherProps} type="geojson" data={sanitizedData} tolerance={.001} />
    )
}
